/* eslint-disable react/prop-types */
import React, { type FC } from "react";
import classNames from "classnames";
import { DetailList, type DetailListColumns, type DetailListLayout, type DetailRowTone } from "@shared/ui/detail-row";
import { SkeletonRow } from "@src/components/skeleton-row";
import "./ListItem.scss";

/** Визуальный тон {@link ListItem} для семантической маркировки строки. */
export type ListItemTone = DetailRowTone;

/** Поле read-only характеристики в {@link ListItem}. */
export interface ListItemProperty {
    /** Подпись характеристики. */
    label: string;
    /** Значение; может быть строкой, числом или React-узлом. */
    value?: React.ReactNode;
}

/** Пропсы компонента {@link ListItem}. */
export interface ListItemProps {
    /** Заголовок элемента списка; может быть строкой или React-узлом. */
    title: React.ReactNode;
    /** Дополнительные read-only характеристики. */
    props?: ListItemProperty[];
    /**
     * Режим отображения характеристик. \
     * `tiles` — сетка плиток через {@link DetailList}; по умолчанию `inline`.
     * @default "inline"
     */
    detailsLayout?: DetailListLayout;
    /**
     * Число колонок для `detailsLayout="tiles"`. \
     * `"auto"` подбирает колонки по ширине и {@link ListItemProps.detailsMinWidth}.
     * @default 3
     */
    detailsColumns?: DetailListColumns;
    /**
     * Минимальная ширина плитки для `detailsColumns="auto"` в пикселях.
     * @default 112
     */
    detailsMinWidth?: number;
    /**
     * Горизонтальная прокрутка плиток в одну строку. \
     * Имеет смысл только при `detailsLayout="tiles"`.
     * @default false
     */
    detailsOneLine?: boolean;
    /**
     * Семантическая подсветка строки.
     * @default "default"
     */
    tone?: ListItemTone;
    /**
     * Неинтерактивная строка: без клика, приглушённый вид. \
     * При `true` обработчик `onClick` не вызывается.
     * @default false
     */
    disabled?: boolean;
    /** Показывает skeleton вместо контента. */
    isLoad?: boolean;
    /**
     * Обработчик клика по элементу списка. \
     * Для `disabled` строк можно не передавать.
     */
    onClick?: () => void;
}

/**
 * Элемент списка с заголовком и набором read-only характеристик.
 *
 * @example
 * ```tsx
 * <ListItem
 *   title="Приходный ордер №12"
 *   detailsLayout="tiles"
 *   detailsColumns="auto"
 *   detailsMinWidth={96}
 *   props={[
 *     { label: "Дата", value: "01.01.2026" },
 *     { label: "Статус", value: "В работе" },
 *   ]}
 *   onClick={() => {}}
 * />
 * ```
 */
export const ListItem: FC<ListItemProps> = ({
    title,
    props,
    detailsLayout = "inline",
    detailsColumns,
    detailsMinWidth,
    detailsOneLine,
    tone = "default",
    disabled = false,
    isLoad,
    onClick,
}) => {
    if (isLoad === true) {
        return (
            <div className="list_item_base">
                <div className="list_item_load">
                    <SkeletonRow widthInProcent={75} />
                    <div className="list_item_load_list">
                        <SkeletonRow widthInProcent={50} className="load_list_item" />
                        <SkeletonRow widthInProcent={30} className="load_list_item" />
                        <SkeletonRow widthInProcent={40} className="load_list_item" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={classNames(
                "list_item_base",
                detailsLayout === "tiles" && "list_item_base--tiles",
                tone !== "default" && `list_item_base--${tone}`,
                disabled && "list_item_base--disabled",
            )}
            aria-disabled={disabled || undefined}
            onClick={disabled ? undefined : onClick}
        >
            <div className="list_item_header">
                <div className="list_item_title">{title}</div>
            </div>
            {props != null && (
                <DetailList
                    className="list_item_props_list"
                    layout={detailsLayout}
                    columns={detailsColumns}
                    minWidth={detailsMinWidth}
                    oneLine={detailsOneLine}
                >
                    {props.map((prop, index) => (
                        <DetailList.Row key={index} label={prop.label} value={prop.value ?? "Не задано"} />
                    ))}
                </DetailList>
            )}
        </div>
    );
};
